/**
 * materialPublish.ts — turning a page's authored material into records.
 *
 * A document's material is a *companion record*, not a field on the post: the
 * clips and the questions are far bigger than anything else a post carries, and
 * a reader who is scrolling a feed has not asked for them. The post carries the
 * page table and where the record is; the record is fetched when somebody plays.
 *
 * Three decisions live here, and each one is the difference between "works" and
 * "works the second time".
 *
 *  - **The clips are made durable first, and referenced.** A take recorded while
 *    signed out, or picked while offline, is a `file:` / `blob:` / `data:` URL
 *    that only this device can open — publishing one of those would ship a page
 *    nobody else can hear. Every local URL is uploaded and swapped for its public
 *    address before the record is built, so the record only ever holds addresses
 *    that work for everyone. This is the same rule a FlashBook's reading audio
 *    follows, and it is applied per *page* here rather than per block.
 *  - **The record also references the bytes.** Uploading is only half of making a
 *    clip durable: a PDS parks an upload until a record's own blob reference
 *    claims it, and serves it only from then on (see the note on
 *    `DocumentPageMaterial.audio`). So the clip is written as a blob reference as
 *    well as an address — one fact about where the bytes are and one about what
 *    they are called — and a clip whose bytes are only named is a clip the server
 *    may refuse to serve the moment it is published.
 *  - **The record rides in the same commit as the post.** `prepareDocumentMaterial`
 *    returns an `applyWrites` operation, and the caller appends it to the batch
 *    that creates the post. One commit means there is no window where a published
 *    post names material that does not exist — and no second write to fail after
 *    the post has already gone out.
 *  - **That batch is the one batch written without `validate: true`.** The
 *    material's collection has no lexicon in this deployment yet, and asking the
 *    PDS to *require* validation would have it refuse the record outright
 *    (`Unknown lexicon type`). Leaving `validate` unset is the documented middle
 *    path — "validate only for known lexicons" — so the post, its gates and every
 *    other record in the batch are still validated, and only the material rides
 *    through with `validationStatus: 'unknown'`.
 */
import {
  type $Typed,
  type AtpAgent,
  type ComAtprotoRepoApplyWrites,
} from '@atproto/api'
import {t} from '@lingui/core/macro'

import {uploadBlob} from '#/lib/api/upload-blob'
import {clipSourceFor, isLocalClipUrl} from '#/lib/flashbookClips'
import {logger} from '#/logger'
import {
  buildDocumentMaterialRecord,
  DOCUMENT_MATERIAL_RECORD_TYPE,
  type DocumentMaterial,
  type DocumentMaterialRecord,
  materialPagesOf,
  normalizeDocumentMaterial,
  trimMaterialToBudget,
} from './material'
import {buildPdsBlobUrl} from './util'

export {buildDocumentMaterialRecord}

/** Everything the post's writer needs from a page's material, once prepared. */
export type PreparedDocumentMaterial = {
  /** The material as the record holds it, with every clip's address public. */
  material: DocumentMaterial
  record: DocumentMaterialRecord
  /** The `applyWrites` operation that creates the record. */
  write: $Typed<ComAtprotoRepoApplyWrites.Create>
  /** What the post's embed carries, so the badges need no fetch. */
  embed: {
    materialUri: string
    materialPages: {page: number; audio: number; questions: number}[]
  }
  /** Pages given up to fit the record budget, for the publish path to report. */
  droppedPages: number[]
  /** True when a local clip was uploaded here rather than already published. */
  uploadedClips: boolean
}

/**
 * Builds the material record for a set of pages. Kept separate from the upload
 * step so the record's shape can be unit tested without an agent.
 */
export function buildDocumentMaterialRecordEntry({
  material,
  documentUri,
  createdAt,
  rkey,
}: {
  material: DocumentMaterial
  documentUri: string
  createdAt: string
  rkey: string
}): {
  record: DocumentMaterialRecord
  write: $Typed<ComAtprotoRepoApplyWrites.Create>
} {
  const record = buildDocumentMaterialRecord({material, documentUri, createdAt})
  return {
    record,
    write: {
      $type: 'com.atproto.repo.applyWrites#create',
      collection: DOCUMENT_MATERIAL_RECORD_TYPE,
      rkey,
      value: record,
    },
  }
}

/**
 * Uploads every page's clip and returns the material with a public address *and*
 * a blob reference for each one — the same material, or one with nothing left to
 * send.
 *
 * A page's clip is one URL, not a list, so this is a flat pass over the pages.
 * Two kinds of clip are uploaded:
 *
 *  - one whose URL is still local (`file:` / `blob:` / `data:`), whose bytes are
 *    the tab's; and
 *  - one that has already been uploaded but whose bytes this session still holds,
 *    because a clip's field uploads at the moment of capture and remembers the
 *    local URL it came from (`#/lib/flashbookClips`). Re-uploading it is free in
 *    every way that matters — the CID is a hash of the bytes, so the same clip
 *    keeps the same address — and it is the only way to get back the *reference*
 *    that makes the upload permanent, which the field's upload did not keep.
 *
 * A clip whose bytes are gone — a URL that is already public and a session that
 * no longer holds the file — is left as the address it is. That clip is the one
 * case this cannot promise: the record still names it, and the page still plays
 * wherever the deployment serves its blobs by address.
 */
export async function flushLocalPageAudio({
  agent,
  did,
  material,
  onStateChange,
}: {
  agent: AtpAgent
  did: string
  material: DocumentMaterial
  onStateChange?: (state: string) => void
}): Promise<{material: DocumentMaterial; uploadedClips: boolean}> {
  let uploadedClips = false
  const pages: DocumentMaterial['pages'] = []
  for (const page of material.pages) {
    if (!page.audioUrl) {
      pages.push(page)
      continue
    }
    // Already referenced: the bytes are claimed and the address is derived from
    // them, so there is nothing this pass could add.
    if (page.audio) {
      pages.push(page)
      continue
    }
    const source = clipSourceFor(page.audioUrl)
    if (!isLocalClipUrl(page.audioUrl) && !isLocalClipUrl(source)) {
      pages.push(page)
      continue
    }
    onStateChange?.(t`Uploading page audio...`)
    const res = await uploadBlob(agent, source)
    uploadedClips = true
    const blob = res.data.blob
    const cid = blob.ref.toString()
    pages.push({
      ...page,
      audioUrl: buildPdsBlobUrl(did, cid),
      audio: {
        $type: 'blob',
        ref: {$link: cid},
        mimeType: blob.mimeType ?? 'audio/mpeg',
        size: typeof blob.size === 'number' ? blob.size : 0,
      },
    })
  }
  return {material: {pages}, uploadedClips}
}

/**
 * The material a post is about to publish, or `undefined` when it has none.
 *
 * `uri` is the address the material record *will* have, built by the caller from
 * the rkey it has already chosen — the record is written in the same commit as
 * the post, so there is nothing to wait for and no second pass to patch the post
 * afterwards.
 */
export async function prepareDocumentMaterial({
  agent,
  did,
  material,
  uri,
  rkey,
  documentUri,
  now,
  onStateChange,
}: {
  agent: AtpAgent
  did: string
  /** The authored material, as the composer holds it. */
  material: DocumentMaterial | undefined
  /** The address of the record that will hold it. */
  uri: string
  rkey: string
  /** The post the material belongs to. */
  documentUri: string
  now: Date
  onStateChange?: (state: string) => void
}): Promise<PreparedDocumentMaterial | undefined> {
  const normalized = normalizeDocumentMaterial(material)
  if (!normalized) return undefined

  const flushed = await flushLocalPageAudio({
    agent,
    did,
    material: normalized,
    onStateChange,
  })

  const createdAt = now.toISOString()
  const {material: trimmed, droppedPages} = trimMaterialToBudget(
    flushed.material,
    {
      // The record's *whole* size, including its own fields, so the budget means
      // what it says rather than "the pages fit and the record nearly did".
      sizeOf: pages =>
        JSON.stringify({$type: DOCUMENT_MATERIAL_RECORD_TYPE, pages}).length,
    },
  )
  if (droppedPages.length > 0) {
    logger.warn('Document material exceeded the record budget', {droppedPages})
  }
  if (trimmed.pages.length === 0) return undefined

  const {record, write} = buildDocumentMaterialRecordEntry({
    material: trimmed,
    documentUri,
    createdAt,
    rkey,
  })
  return {
    material: trimmed,
    record,
    write,
    embed: {materialUri: uri, materialPages: materialPagesOf(trimmed)},
    droppedPages,
    uploadedClips: flushed.uploadedClips,
  }
}
